package one.healthium.data.user.storage

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.fromMilliseconds
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import one.healthium.data.user.UserStorage
import one.healthium.data.user.dto.FoodLogDto
import one.healthium.data.user.dto.HealthLogDto
import one.healthium.data.user.dto.PeriodLogDto
import one.healthium.data.user.dto.SymptomLogDto
import one.healthium.data.user.dto.UserDto
import one.healthium.data.user.dto.WaterLogDto
import one.healthium.data.user.dto.WeightLogDto
import one.healthium.data.user.mapper.FoodLogMapper
import one.healthium.data.user.mapper.UserMapper
import one.healthium.domain.common.DateUtils
import one.healthium.domain.health_log.models.HealthLogOption
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.models.FoodItemModel
import one.healthium.domain.user.models.FoodOptions
import one.healthium.domain.user.models.SymptomsLogOptions
import one.healthium.domain.user.models.UserProfileModel
import one.healthium.domain.user.models.UserProfileOptions
import one.healthium.domain.user.models.UserSystemModel
import kotlin.time.Instant
import kotlin.uuid.Uuid

internal class FirebaseUserStorage(
    private val userMapper: UserMapper,
    private val foodLogMapper: FoodLogMapper,
) : UserStorage {

    private val db = Firebase.firestore.apply {
        setLoggingEnabled(false)
    }

    private val users = db.collection("users")

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun get(id: String): Flow<UserModel?> {
        val documentReference = users.document(id)

        return documentReference.snapshots
            .flatMapLatest { userDoc ->
                if (userDoc.exists) {
                    val userDto = userDoc.data(UserDto.serializer())

                    @Suppress("UNCHECKED_CAST")
                    combine(
                        waterLogs(documentReference),
                        weightLogs(documentReference),
                        foodLogs(documentReference),
                        periodLogs(documentReference),
                        symptomsLogs(documentReference),
                        healthLogs(documentReference),
                    ) { values ->
                        val waterLogs = values[0] as List<WaterLogDto>
                        val weightLogs = values[1] as List<WeightLogDto>
                        val foodLogs = values[2] as List<FoodLogDto>
                        val periodLogs = values[3] as PeriodLogDto
                        val symptomsLogs = values[4] as List<SymptomLogDto>
                        val healthLog = values[5] as List<HealthLogDto>
                        userMapper.mapUserDto(id, userDto, waterLogs, weightLogs, foodLogs, periodLogs, symptomsLogs, healthLog)
                    }
                } else {
                    flowOf(null)
                }
            }
    }

    override suspend fun update(userId: String, data: UserModel): UserModel {
        val dto = userMapper.mapUserModel(data)
        val documentReference = users.document(userId)
        if (documentReference.get().exists) {
            documentReference.update(UserDto.serializer(), dto) {
                encodeDefaults = false
            }
        } else {
            documentReference.set(UserDto.serializer(), dto) {
                encodeDefaults = false
            }
        }
        data.weightLogs.forEach { weightLog ->
            setWeightForDate(userId, weightLog.date, weightLog.value)
        }
        data.waterLogs.forEach { waterLog ->
            setWaterIntakeForDate(userId, waterLog.date, waterLog.value)
        }
        data.foodLogs.forEach { foodLog ->
            setFoodForDate(userId, foodLog.date, foodLog.food)
        }
        setPeriods(userId, data.periodLogs.periods)
        return get(userId).first() ?: data
    }

    @Deprecated("use UserProfileOptions")
    override suspend fun updateProfile(userId: String, data: UserProfileModel) {
        val dto = UserDto(userMapper.mapProfileModel(data))
        val document = users.document(userId)
        document.update(UserDto.serializer(), dto) {
            encodeDefaults = false
        }
    }

    override suspend fun updateProfile(userId: String, options: UserProfileOptions) {
        val dto = UserDto(userMapper.mapProfileOptions(options))
        val document = users.document(userId)
        document.set(UserDto.serializer(), dto, merge = true) {
            encodeDefaults = false
        }
    }

    override suspend fun addWaterIntake(userId: String, value: Float) {
        val document = users.document(userId)
        if (document.get().exists) {
            val todayWaterLogDocument = todayWaterLogReference(document)
            if (todayWaterLogDocument.get().exists) {
                val waterLog = todayWaterLogDocument.get().data(WaterLogDto.serializer())
                val updatedWaterLog = waterLog.copy(
                    water = waterLog.water + value
                )
                todayWaterLogDocument.update(WaterLogDto.serializer(), updatedWaterLog) {
                    encodeDefaults = false
                }
            } else {
                val waterLog = WaterLogDto(
                    date = Timestamp.now(),
                    water = value,
                )
                todayWaterLogDocument.set(WaterLogDto.serializer(), waterLog) {
                    encodeDefaults = false
                }
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    suspend fun setWaterIntakeForDate(userId: String, date: Instant, value: Float) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val waterLogDocument = waterLogReference(userDocument, DateUtils.documentNameFormatted(date))
            val waterLog = WaterLogDto(
                date = date.toTimestamp(),
                water = value,
            )
            waterLogDocument.set(WaterLogDto.serializer(), waterLog) {
                encodeDefaults = false
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    override suspend fun addWeight(userId: String, value: Float) {
        val document = users.document(userId)
        if (document.get().exists) {
            val todayWeightLogDocument = todayWeightLogReference(document)
            val weightLog = WeightLogDto(
                date = Timestamp.now(),
                weight = value,
            )
            todayWeightLogDocument.set(WeightLogDto.serializer(), weightLog) {
                encodeDefaults = false
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    suspend fun setWeightForDate(userId: String, date: Instant, value: Float) {
        val document = users.document(userId)
        if (document.get().exists) {
            val weightLogDocument = weightLogReference(document, DateUtils.documentNameFormatted(date))
            val weightLog = WeightLogDto(
                date = date.toTimestamp(),
                weight = value,
            )
            weightLogDocument.set(WeightLogDto.serializer(), weightLog) {
                encodeDefaults = false
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    suspend fun removeWeight(userId: String, date: Instant) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val weightLogDocument = weightLogReference(userDocument, DateUtils.documentNameFormatted(date))
            if (weightLogDocument.get().exists) {
                weightLogDocument.delete()
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    suspend fun addFood(userId: String, options: FoodOptions) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val foodItemDto = foodLogMapper.optionsToDto(Uuid.random().toString(), options)
            val date = options.date
            val documentName = DateUtils.documentNameFormatted(date)
            val todayFoodLogDocument = foodLogReference(userDocument, documentName)
            if (todayFoodLogDocument.get().exists) {
                val foodLog = todayFoodLogDocument.get().data(FoodLogDto.serializer())
                val updatedFoodLog = foodLog.copy(
                    food = foodLog.food + foodItemDto
                )
                todayFoodLogDocument.update(FoodLogDto.serializer(), updatedFoodLog) {
                    encodeDefaults = false
                }
            } else {
                val foodLog = FoodLogDto(
                    id = documentName,
                    date = date.toTimestamp(),
                    food = listOf(foodItemDto),
                )
                todayFoodLogDocument.set(FoodLogDto.serializer(), foodLog) {
                    encodeDefaults = false
                }
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    private suspend fun setFoodForDate(userId: String, date: Instant, food: List<FoodItemModel>) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val foodLogDocument = foodLogReference(userDocument, DateUtils.documentNameFormatted(date))
            val foodLog = FoodLogDto(
                id = DateUtils.documentNameFormatted(date),
                date = date.toTimestamp(),
                food = food.map(foodLogMapper::modelToDto)
            )
            foodLogDocument.set(FoodLogDto.serializer(), foodLog) {
                encodeDefaults = false
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    suspend fun setFoodForDate(userId: String, dayId: String, foodId: String, options: FoodOptions) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val foodLogDocument = foodLogReference(userDocument, dayId)
            if (foodLogDocument.get().exists) {
                val foodLog = foodLogDocument.get().data(FoodLogDto.serializer())
                val updatedFoodLog = foodLog.copy(
                    food = foodLog.food.map { foodItemDto ->
                        if (foodItemDto.id == foodId) {
                            foodItemDto.copy(
                                amount = options.amount,
                                calories = options.calories,
                                fat = options.fat,
                                carbohydrates = options.carbohydrates,
                                fiber = options.fiber,
                                protein = options.protein,
                                unit = foodLogMapper.foodUnitToString(options.unit)
                            )
                        } else {
                            foodItemDto
                        }
                    }
                )
                foodLogDocument.update(FoodLogDto.serializer(), updatedFoodLog) {
                    encodeDefaults = false
                }
            } else {
                throw IllegalStateException("food log does not exist")
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    suspend fun removeFood(userId: String, dayId: String, foodId: String) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val foodLogDocument = foodLogReference(userDocument, dayId)
            if (foodLogDocument.get().exists) {
                val foodLog = foodLogDocument.get().data(FoodLogDto.serializer())
                val updatedFoodLog = foodLog.copy(
                    food = foodLog.food.filter { it.id != foodId }
                )
                foodLogDocument.update(FoodLogDto.serializer(), updatedFoodLog) {
                    encodeDefaults = false
                }
            } else {
                throw IllegalStateException("food log does not exist")
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    override suspend fun setPeriods(userId: String, data: List<Instant>) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val periodLogDocument = periodLogReference(userDocument)
            val periodLog = PeriodLogDto(
                periods = data.map { it.toTimestamp() }
            )
            periodLogDocument.set(PeriodLogDto.serializer(), periodLog) {
                encodeDefaults = false
            }
        } else {
            throw IllegalStateException("user does not exist")
        }
    }

    override suspend fun updatePlayTime(userId: String, status: Int) {
        val model = UserSystemModel(
            playTime = status
        )
        val dto = UserDto(system = userMapper.mapSystemModel(model))
        val document = users.document(userId)
        document.update(UserDto.serializer(), dto) {
            encodeDefaults = false
        }
    }

    suspend fun addSymptoms(userId: String, options: SymptomsLogOptions) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val symptomsLogDto = userMapper.mapSymptomOptions(options)
            val todayDocumentName = DateUtils.todayDocumentNameFormatted()
            val todaySymptomsLogsDocument = symptomsLogsReference(userDocument, todayDocumentName)
            todaySymptomsLogsDocument.set(SymptomLogDto.serializer(), symptomsLogDto) {
                encodeDefaults = false
            }
        } else {
            throw IllegalStateException("user does not exist")
        }

    }

    suspend fun addHealthLog(userId: String, options: List<HealthLogOption>) {
        val userDocument = users.document(userId)
        if (userDocument.get().exists) {
            val dto = userMapper.mapHealthLogOptions(options)
            val todayDocumentName = DateUtils.todayDocumentNameFormatted()
            val todayHealthLogsDocument = healthLogsReference(userDocument, todayDocumentName)
            todayHealthLogsDocument.set(HealthLogDto.serializer(), dto) {
                encodeDefaults = false
            }
        } else {
            throw IllegalStateException("user does not exist")
        }

    }

    private fun todayWaterLogReference(userDocument: DocumentReference): DocumentReference {
        val documentName = DateUtils.todayDocumentNameFormatted()
        return waterLogReference(userDocument, documentName)
    }

    private fun waterLogReference(userDocument: DocumentReference, documentName: String): DocumentReference {
        return userDocument.collection("water_log").document(documentName)
    }

    private fun waterLogs(userDocument: DocumentReference): Flow<List<WaterLogDto>> {
        return userDocument.collection("water_log")
            .orderBy(WaterLogDto.FIELD_DATE, Direction.DESCENDING)
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { it.data(WaterLogDto.serializer()) }
            }
    }

    private fun periodLogs(userDocument: DocumentReference): Flow<PeriodLogDto> {
        return periodLogReference(userDocument)
            .snapshots
            .map {
                if (it.exists) {
                    it.data(PeriodLogDto.serializer())
                } else {
                    PeriodLogDto(emptyList())
                }
            }
    }

    private fun weightLogs(userDocument: DocumentReference): Flow<List<WeightLogDto>> {
        return userDocument.collection("weight_log")
            .orderBy(WeightLogDto.FIELD_DATE, Direction.DESCENDING)
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { it.data(WeightLogDto.serializer()) }
            }
    }

    private fun todayWeightLogReference(userDocument: DocumentReference): DocumentReference {
        val documentName = DateUtils.todayDocumentNameFormatted()
        return weightLogReference(userDocument, documentName)
    }

    private fun weightLogReference(userDocument: DocumentReference, documentName: String): DocumentReference {
        return userDocument.collection("weight_log").document(documentName)
    }

    private fun foodLogReference(userDocument: DocumentReference, documentName: String): DocumentReference {
        return userDocument.collection("food_log_v2").document(documentName)
    }

    private fun foodLogs(userDocument: DocumentReference): Flow<List<FoodLogDto>> {
        return userDocument.collection("food_log_v2")
            .orderBy(FoodLogDto.FIELD_DATE, Direction.DESCENDING)
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { document ->
                    document.data(FoodLogDto.serializer()).addIdIfNeeded(document.id)
                }
            }
    }

    private fun symptomsLogsReference(userDocument: DocumentReference, documentName: String): DocumentReference {
        return userDocument.collection("symptoms_log").document(documentName)
    }

    private fun symptomsLogs(userDocument: DocumentReference): Flow<List<SymptomLogDto>> {
        return userDocument.collection("symptoms_log")
            .orderBy(SymptomLogDto.FIELD_DATE, Direction.DESCENDING)
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { document ->
                    document.data(SymptomLogDto.serializer())
                }
            }
    }

    private fun healthLogsReference(userDocument: DocumentReference, documentName: String): DocumentReference {
        return userDocument.collection("health_log").document(documentName)
    }

    private fun healthLogs(userDocument: DocumentReference): Flow<List<HealthLogDto>> {
        return userDocument.collection("health_log")
            .orderBy(HealthLogDto.FIELD_DATE, Direction.DESCENDING)
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { document ->
                    document.data(HealthLogDto.serializer())
                }
            }
    }

    // id was added later so we have to add this workaround for old items
    private fun FoodLogDto.addIdIfNeeded(id: String): FoodLogDto {
        return if (this.id == null) {
            this.copy(id = id)
        } else {
            this
        }
    }

    private fun periodLogReference(userDocument: DocumentReference): DocumentReference {
        return userDocument.collection("period_log").document("data")
    }
}

internal fun Instant.toTimestamp() = Timestamp.fromMilliseconds(toEpochMilliseconds().toDouble())